import { getLexicalArgKeys } from '../getLexicalArgKeys.js';
import { appSchema } from '../../schema/index.js';
import { getArgumentValue } from '../getArgumentValue.js';

const OPERATOR_CACHE = new Map();
const getOperatorShorthand = (operator) => {
  if (OPERATOR_CACHE.has(operator)) {
    return OPERATOR_CACHE.get(operator);
  }
  const operators = appSchema?.api?.filters?.operators;
  if (!operators) {
    return undefined;
  }
  for (let i = 0; i < operators.length; i++) {
    if (operators[i]?.value === operator) {
      const shorthand = operators[i]?.shorthand;
      OPERATOR_CACHE.set(operator, shorthand);
      return shorthand;
    }
  }
  return undefined;
};

const UNARY_OPERATORS = new Set(['exist', 'nexist', 'empty', 'nempty']);
const BETWEEN_OPERATOR = 'between';

const evaluateCondition = async ({ field, operator, value, steps }) => {
  const fieldValue = field?.value;
  if (!fieldValue) {
    return '...';
  }

  const fieldParts = getLexicalArgKeys(fieldValue);
  if (!fieldParts || fieldParts.length === 0) {
    return '...';
  }

  let stepsMap = null;
  const stepsArr = steps || [];
  const hasBlockParts = fieldParts.some((part) => part.type === 'block');

  if (hasBlockParts && stepsArr.length > 10) {
    stepsMap = new Map();
    for (let i = 0; i < stepsArr.length; i++) {
      const step = stepsArr[i];
      if (step?.id) {
        stepsMap.set(step.id, step);
      }
    }
  }

  const fieldContent = fieldParts
    .map((part) => {
      if (part.type === 'block') {
        const step = stepsMap ? stepsMap.get(part.stepId) : stepsArr.find((s) => s?.id === part.stepId);
        const stepNumber = step?.stepNumber;
        return `${stepNumber || ''} ${part.value}`.trim();
      }
      return part.value;
    })
    .join(' ');

  const operatorSchema = getOperatorShorthand(operator);

  if (UNARY_OPERATORS.has(operator)) {
    return `${fieldContent} ${operatorSchema || ''}`;
  }

  if (operator === BETWEEN_OPERATOR) {
    const valueArr = value || [];
    if (valueArr.length === 0) {
      return `${fieldContent} ${operatorSchema || ''}`;
    }
    const values = await Promise.all(valueArr.map((v) => getArgumentValue({ arg: v, opts: { format: 'human' } })));
    return `${fieldContent} is between ${values?.[0] || '...'} and ${values?.[1] || '...'}`;
  }

  const valueArr = value || [];
  let formattedValue;
  if (Array.isArray(valueArr) && valueArr.length > 0) {
    formattedValue = valueArr.length === 1 ? await getArgumentValue({ arg: valueArr[0], opts: { format: 'human' } }) : `${valueArr.length} Values`;
  } else {
    formattedValue = (await getArgumentValue({ arg: value, opts: { format: 'human' } })) || '...';
  }

  return `${fieldContent} ${operatorSchema || ''} ${formattedValue}`;
};

const evaluateFilterSet = async ({ filterSet, conjunction, steps }) => {
  const filterSetArr = filterSet || [];
  if (filterSetArr.length === 0) {
    return '';
  }

  const conditions = await Promise.all(
    filterSetArr.map(async (condition) => {
      if (condition.type === 'nested') {
        const nestedCount = condition.filterSet?.length || 0;
        return `${nestedCount} nested condition${nestedCount === 1 ? '' : 's'}`;
      }
      return await evaluateCondition({
        field: condition.field,
        operator: condition.operator,
        value: condition.value,
        steps
      });
    })
  );

  const joinWord = conjunction === 'or' ? ' or ' : ' and ';
  return conditions.join(joinWord);
};

const evaluateIfElseMessage = async ({ step, args, workflow }) => {
  const steps = workflow?.steps;

  let result = '';

  // Handle new format (conditions object)
  if (args?.conditions?.filterSet?.length > 0) {
    result = `If ${await evaluateFilterSet({
      filterSet: args.conditions.filterSet,
      conjunction: args.conditions.conjunction,
      steps
    })}`;
  } else {
    // Handle old format (direct field, operator, value)
    result = `If ${await evaluateCondition({
      field: args?.field,
      operator: step?.arguments?.operator,
      value: args?.value,
      steps
    })}`;
  }

  if (result === 'If ...') {
    return 'If / Else';
  }

  return result;
};

export default evaluateIfElseMessage;
